import React, { useEffect, useMemo, useState } from 'react';
import { bankAccountApi, investmentApi } from '../services/api';
import { BankAccount, CreateInvestmentRequest } from '../types';
import DateInput from './DateInput';
import Alert from './Alert';

interface InvestmentFormModalProps {
    onSuccess: () => void;
    onCancel: () => void;
}

const InvestmentFormModal: React.FC<InvestmentFormModalProps> = ({ onSuccess, onCancel }) => {
    const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
    const [form, setForm] = useState<{ bankAccountId: string; amount: number; occurredAt: string; type: 'credit' | 'debit' | 'yield'; }>({
        bankAccountId: '',
        amount: 0,
        occurredAt: '',
        type: 'credit',
    });
    const [amountInput, setAmountInput] = useState<string>('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const nowIso = new Date().toISOString();
        setForm((f) => ({ ...f, occurredAt: nowIso }));
        const loadAccounts = async () => {
            try {
                const res = await bankAccountApi.getAll();
                const accounts = res.data || [];
                // Filter only vault accounts (isVault === true)
                const vaultAccounts = accounts.filter(acc => acc.isVault === true);
                setBankAccounts(vaultAccounts);
                if (vaultAccounts.length > 0) {
                    setForm((f) => ({ ...f, bankAccountId: f.bankAccountId || vaultAccounts[0].id }));
                }
            } catch (e) {
                // silently ignore
            }
        };
        loadAccounts();
    }, []);

    const formatBRL = (value: number) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
    const parseToNumber = (raw: string) => {
        const onlyDigits = raw.replace(/\D/g, '');
        if (!onlyDigits) return 0;
        const cents = parseInt(onlyDigits, 10);
        return cents / 100;
    };

    useEffect(() => {
        setAmountInput(form.amount ? formatBRL(form.amount) : '');
    }, []);

    const isValid = useMemo(() => {
        return (
            !!form.bankAccountId &&
            typeof form.amount === 'number' && form.amount > 0 &&
            !!form.occurredAt &&
            (form.type === 'credit' || form.type === 'debit' || form.type === 'yield')
        );
    }, [form]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        if (!isValid) {
            setError('Preencha todos os campos obrigatórios. O valor deve ser maior que zero.');
            return;
        }
        try {
            setSubmitting(true);
            const cents = Math.round(form.amount * 100);
            const payload: CreateInvestmentRequest = {
                bankAccountId: form.bankAccountId,
                amount: cents,
                occurredAt: form.occurredAt,
                type: form.type,
            };
            await investmentApi.create(payload);
            onSuccess();
        } catch (e: any) {
            const friendly = e?.response?.data?.translatedMessage || 'Falha ao registrar investimento.';
            setError(friendly);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            {error && (<Alert type="error" title="Não foi possível concluir" message={error} />)}

            <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">Conta Bancária *</label>
                <select
                    className="input-field"
                    value={form.bankAccountId}
                    onChange={(e) => setForm({ ...form, bankAccountId: e.target.value })}
                    required
                >
                    {bankAccounts.map(acc => (
                        <option key={acc.id} value={acc.id}>{acc.name}</option>
                    ))}
                </select>
            </div>

            <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">Tipo *</label>
                <div className="inline-flex rounded-md shadow-sm" role="group">
                    <button
                        type="button"
                        className={`px-4 py-2 text-sm font-medium border border-secondary-300 ${form.type === 'credit' ? 'bg-primary-600 text-white' : 'bg-white text-secondary-800'} rounded-l-md transition-colors`}
                        onClick={() => setForm({ ...form, type: 'credit' })}
                        aria-pressed={form.type === 'credit'}
                    >
                        Investimento
                    </button>
                    <button
                        type="button"
                        className={`px-4 py-2 text-sm font-medium border-t border-b border-secondary-300 ${form.type === 'debit' ? 'bg-primary-600 text-white' : 'bg-white text-secondary-800'} transition-colors`}
                        onClick={() => setForm({ ...form, type: 'debit' })}
                        aria-pressed={form.type === 'debit'}
                    >
                        Resgate
                    </button>
                    <button
                        type="button"
                        className={`px-4 py-2 text-sm font-medium border-t border-b border-r border-secondary-300 ${form.type === 'yield' ? 'bg-primary-600 text-white' : 'bg-white text-secondary-800'} rounded-r-md transition-colors`}
                        onClick={() => setForm({ ...form, type: 'yield' })}
                        aria-pressed={form.type === 'yield'}
                    >
                        Rendimento
                    </button>
                </div>
            </div>

            <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">Valor *</label>
                <input
                    type="text"
                    className="input-field"
                    value={amountInput}
                    onChange={(e) => {
                        const raw = e.target.value || '';
                        const numberValue = parseToNumber(raw);
                        setAmountInput(formatBRL(numberValue));
                        setForm({ ...form, amount: numberValue });
                    }}
                    placeholder="R$ 0,00"
                    inputMode="numeric"
                    required
                />
            </div>

            <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">Data *</label>
                <DateInput
                    value={form.occurredAt}
                    onChange={(v) => setForm({ ...form, occurredAt: v || form.occurredAt })}
                    name="occurredAt"
                />
            </div>

            <div className="flex justify-end space-x-3 pt-4">
                <button type="button" onClick={onCancel} className="btn-secondary">Cancelar</button>
                <button type="submit" className="btn-primary" disabled={submitting || !isValid}>
                    {submitting ? 'Enviando...' : 'Registrar'}
                </button>
            </div>
        </form>
    );
};

export default InvestmentFormModal;

